import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import authService from '../services/auth.service';
import { clearAllAuthData } from '../utils/authUtils';
import { AuthContextType } from '../types/context.d';
import { LocalStorageUser } from '../types/localStorage.d';
import { AuthResponse, ApiResponse, LoginCredentials, CreateUserResponse, CreateUserRequest, ActivateUserResponse, LoginResponse, SendOtpRequest, BasicResponse } from '../types/api.d';

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<LocalStorageUser | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [loading, setLoading] = useState<boolean>(true);

  const refreshUser = async () => {
    const storedToken = localStorage.getItem('token');
    const storedUser = JSON.parse(localStorage.getItem('user')|| "{}");
    if (storedToken) {
      try {
        const userData = await authService.getMe();
        if(userData){
          // update keys from api response
          setUser({...storedUser , ...userData});
          setToken(storedToken);
        } else {
          // clear token on expiry
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setToken(null);
          setUser(null);

        }
      } catch (error) {
        // Token is invalid, clear it
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setToken(null);
        setUser(null);
      }
    }
    setLoading(false);
  };

  // Check if user is authenticated on mount
  useEffect(() => {
    refreshUser();
  }, []);

  const login = async (credentials: LoginCredentials): Promise<AuthResponse | undefined> => {
    try {
      setLoading(true);
      const response = await authService.login(credentials);
      const {token} = response.data
      if (token) {
        localStorage.setItem('token', token);
        setToken(token);
        localStorage.setItem('user', JSON.stringify(response.data));
        const userDetails = await authService.getMe();
        localStorage.setItem('user', JSON.stringify(userDetails));
        setUser(userDetails);
        return {token , user: userDetails};
      }
      return undefined;
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = (): void => {
    // Clear all authentication data
    authService.logout(); // Assuming authService.logout also clears its internal state if any
    clearAllAuthData();
    setToken(null);
    setUser(null);
  };

  const register = async (userData: CreateUserRequest): Promise<CreateUserResponse> => {
    try {
      setLoading(true);
      const {data:response} = await authService.register(userData);
      if (response) {
        // Normalize role to lowercase for routing
        const normalizedUser: LocalStorageUser = {
          ...userData,
          ...response,
        };
        localStorage.setItem('user', JSON.stringify(normalizedUser));
        setUser(normalizedUser);
        return response;
      }
      return response;
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const verifyOTP = async (otpData: any): Promise<boolean> => {
    try {
      setLoading(true);
      const response= await authService.verifyOTP(otpData);
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };
  const verifyEmailOTP = async (otpData: any): Promise<boolean> => {
    try {
      const response= await authService.verifyOTP(otpData);
      return response.data;
    } catch (error) {
      throw error;
    } finally {
    }
  };

  const setupPassword = async (passwordData: any): Promise<AuthResponse | undefined> => {
    try {
      setLoading(true);
      const response = await authService.setupPassword(passwordData);
      const {token} = response.data
      if (token) {
        localStorage.setItem('token', token);
        setToken(token);
        localStorage.setItem('user', JSON.stringify(response.data));
        const userDetails = await authService.getMe();
        localStorage.setItem('user', JSON.stringify(userDetails));
        setUser(userDetails);
        return {token , user: userDetails};
      }
      return undefined;
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
    return undefined;
  };

  const forgotPassword = async (mobileNumber: string): Promise<{userId: string}> => {
    try {
      setLoading(true);
      const response = await authService.forgotPassword(mobileNumber);
      return {userId : response.data};
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const sendOTP = async (otpRequest: SendOtpRequest): Promise<string> => {
    try {
      const response = await authService.sendOTP(otpRequest);
      return response.data;
    } catch (error) {
      throw error;
    } finally {
    }
  };

  const updateUser = (userData: Partial<LocalStorageUser>): void => {
    setUser(prevUser => {
      if (!prevUser) return null;
      const updatedUser = { ...prevUser, ...userData };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      return updatedUser;
    });
  };

  const value: AuthContextType = {
    user,
    token,
    loading,
    login,
    logout,
    register,
    verifyOTP,
    verifyEmailOTP,
    sendOTP, // Expose sendOTP
    setupPassword,
    forgotPassword,
    isAuthenticated: !!token && !!user,
    updateUser, // Add updateUser to the context value
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
